import React, { useEffect, useRef, useState } from "react";
import { http, requestErrorMessage, companySignatureUrl } from "@/lib/erp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PenTool, Trash2, Upload } from "lucide-react";

const MAX_FILE_SIZE = 2 * 1024 * 1024;
const ALLOWED_TYPES = ["image/png", "image/jpeg"];

export default function SignatureSettings({ form, set, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [previewVersion, setPreviewVersion] = useState(Date.now());
  const [hasSignature, setHasSignature] = useState(Boolean(form.has_signature));
  const [success, setSuccess] = useState("");
  const [hasDrawing, setHasDrawing] = useState(false);
  const canvasRef = useRef(null);
  const strokesRef = useRef([]);
  const activeStrokeRef = useRef(null);
  useEffect(() => setHasSignature(Boolean(form.has_signature)), [form.has_signature]);

  const drawStroke = (context, stroke, width, height) => {
    if (!stroke.length) return;
    context.beginPath();
    context.moveTo(stroke[0].x * width, stroke[0].y * height);
    stroke.slice(1).forEach((point) => context.lineTo(point.x * width, point.y * height));
    if (stroke.length === 1) context.lineTo(stroke[0].x * width + 0.1, stroke[0].y * height + 0.1);
    context.stroke();
  };

  const redraw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    const context = canvas.getContext("2d");
    context.clearRect(0, 0, canvas.width / ratio, canvas.height / ratio);
    strokesRef.current.forEach((stroke) => drawStroke(context, stroke, rect.width, rect.height));
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const ratio = window.devicePixelRatio || 1;
      canvas.width = Math.round(rect.width * ratio);
      canvas.height = Math.round(rect.height * ratio);
      const context = canvas.getContext("2d");
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.lineWidth = 2.5;
      context.lineCap = "round";
      context.lineJoin = "round";
      context.strokeStyle = "#172554";
      strokesRef.current.forEach((stroke) => drawStroke(context, stroke, rect.width, rect.height));
    };
    resize();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(resize) : null;
    observer?.observe(canvas);
    window.addEventListener("resize", resize);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, []);

  const getPoint = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height)),
    };
  };
  const startDrawing = (event) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    activeStrokeRef.current = [getPoint(event)];
    strokesRef.current = [...strokesRef.current, activeStrokeRef.current];
    setHasDrawing(true);
    setError("");
    setSuccess("");
    redraw();
  };
  const continueDrawing = (event) => {
    if (!activeStrokeRef.current) return;
    event.preventDefault();
    activeStrokeRef.current.push(getPoint(event));
    redraw();
  };
  const stopDrawing = () => { activeStrokeRef.current = null; };
  const clearDrawing = () => {
    strokesRef.current = [];
    activeStrokeRef.current = null;
    setHasDrawing(false);
    setError("");
    setSuccess("");
    redraw();
  };

  const signature = hasSignature
    ? companySignatureUrl({ ...form, has_signature: true }, previewVersion)
    : null;

  const uploadFile = async (file) => {
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const data = new FormData();
      data.append("file", file, file.name || "signature.png");
      await http.post("/company/signature", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setHasSignature(true);
      setPreviewVersion(Date.now());
      setSuccess("Signature saved.");
      await onRefresh?.();
    } catch (err) {
      setError(requestErrorMessage(err, "save the signature"));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError("Choose a PNG or JPEG image.");
      setSuccess("");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError("The signature image must be 2 MB or smaller.");
      setSuccess("");
      return;
    }

    await uploadFile(file);
  };

  const saveDrawing = () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasDrawing) {
      setError("Draw a signature before saving.");
      return;
    }
    canvas.toBlob(async (blob) => {
      if (!blob) {
        setError("Could not create the signature image. Please try again.");
        return;
      }
      if (blob.size > MAX_FILE_SIZE) {
        setError("The signature image must be 2 MB or smaller.");
        return;
      }
      await uploadFile(new File([blob], "signature.png", { type: "image/png" }));
    }, "image/png");
  };

  const remove = async () => {
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      await http.delete("/company/signature");
      setHasSignature(false);
      setPreviewVersion(Date.now());
      setSuccess("Signature removed.");
      await onRefresh?.();
    } catch (err) {
      setError(requestErrorMessage(err, "remove the signature"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="font-semibold flex items-center gap-2"><PenTool size={16} /> Authorised Signatory</div>
        <p className="text-sm text-slate-500 mt-1">Upload a PNG or JPEG signature image (maximum 2 MB). The image and details appear on A4/A5 invoices when enabled.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="signature-name">Signatory name</Label>
          <Input id="signature-name" value={form.signature_name || ""} className="mt-1.5"
            onChange={(event) => set("signature_name", event.target.value)} />
        </div>
        <div>
          <Label htmlFor="signature-title">Signatory title</Label>
          <Input id="signature-title" value={form.signature_title || ""} className="mt-1.5"
            onChange={(event) => set("signature_title", event.target.value)} />
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={form.print_show_signature !== false}
          onChange={(event) => set("print_show_signature", event.target.checked)} />
        Show authorised signatory on invoices
      </label>

      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex">
          <input type="file" accept="image/png,image/jpeg" className="sr-only" disabled={busy} onChange={upload} />
          <span className={`inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`}>
            <Upload size={15} /> {busy ? "Please wait…" : hasSignature ? "Replace image" : "Upload image"}
          </span>
        </label>
        {hasSignature && (
          <Button type="button" variant="outline" disabled={busy} onClick={remove}>
            <Trash2 size={15} className="mr-2" /> Remove image
          </Button>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="signature-pad">Draw signature</Label>
        <canvas
          id="signature-pad"
          ref={canvasRef}
          aria-label="Signature drawing area. Use a mouse, touchscreen, or stylus to draw."
          role="img"
          className="block h-40 w-full rounded-md border border-slate-300 bg-white touch-none cursor-crosshair"
          onPointerDown={startDrawing}
          onPointerMove={continueDrawing}
          onPointerUp={stopDrawing}
          onPointerCancel={stopDrawing}
          onLostPointerCapture={stopDrawing}
        />
        <div className="flex gap-2">
          <Button type="button" variant="outline" disabled={busy || !hasDrawing} onClick={clearDrawing} data-testid="clear-signature-button">Clear</Button>
          <Button type="button" disabled={busy || !hasDrawing} onClick={saveDrawing} data-testid="save-signature-button">{busy ? "Please wait…" : "Save signature"}</Button>
        </div>
      </div>

      {signature && (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
          <div className="text-xs font-medium text-slate-500 mb-2">Current signature</div>
          <img src={signature} alt="Current authorised signature" className="max-h-24 max-w-full object-contain"
            onError={() => setError("The saved signature image could not be loaded. You can upload a replacement or remove it.")} />
        </div>
      )}
      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      {success && <p role="status" className="text-sm text-emerald-700">{success}</p>}
    </div>
  );
}